import { useEffect, useRef, useState } from "react";

interface Props {
  height?: number;
  onChange?: (hasInk: boolean) => void;
}

export interface SignaturePadHandle {
  toDataURL: () => string;
  clear: () => void;
}

export function useSignaturePad() {
  const ref = useRef<SignaturePadHandle | null>(null);
  return ref;
}

/**
 * Responsive signature pad. The canvas always matches its wrapper's CSS width;
 * the backing buffer is sized by devicePixelRatio for crisp ink. The full
 * stroke history is replayed after every resize so rotating the device or
 * resizing the modal doesn't wipe a signature in progress.
 */
export function SignaturePad({
  height = 180,
  onChange,
  controlRef,
}: Props & { controlRef: React.MutableRefObject<SignaturePadHandle | null> }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  // Persisted stroke history (normalised to 0..1 inside the wrapper width)
  // so resize can re-render without losing the user's ink.
  const strokesRef = useRef<Array<Array<{ xPct: number; yPx: number }>>>([]);
  const currentStrokeRef = useRef<Array<{ xPct: number; yPx: number }> | null>(null);
  const [hasInk, setHasInk] = useState(false);

  function configureCanvas(width: number) {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#0f172a";
    return ctx;
  }

  function repaint() {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const width = wrapper.clientWidth;
    const ctx = configureCanvas(width);
    ctx.clearRect(0, 0, width, height);
    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      stroke.forEach((p, i) => {
        const x = p.xPct * width;
        const y = p.yPx;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current!;
    const wrapper = wrapperRef.current!;
    let observer: ResizeObserver | null = null;

    // Initial paint
    repaint();

    // Re-render on wrapper resize (rotation, modal width change)
    if ("ResizeObserver" in window) {
      observer = new ResizeObserver(() => repaint());
      observer.observe(wrapper);
    }

    function pos(e: PointerEvent): { x: number; y: number; width: number } {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top, width: rect.width };
    }

    function down(e: PointerEvent) {
      e.preventDefault();
      drawingRef.current = true;
      canvas.setPointerCapture(e.pointerId);
      const { x, y, width } = pos(e);
      const stroke = [{ xPct: x / width, yPx: y }];
      currentStrokeRef.current = stroke;
      strokesRef.current.push(stroke);
      const ctx = canvas.getContext("2d")!;
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
    function move(e: PointerEvent) {
      if (!drawingRef.current) return;
      const { x, y, width } = pos(e);
      currentStrokeRef.current?.push({ xPct: x / width, yPx: y });
      const ctx = canvas.getContext("2d")!;
      ctx.lineTo(x, y);
      ctx.stroke();
      if (!hasInk) {
        setHasInk(true);
        onChange?.(true);
      }
    }
    function up() {
      drawingRef.current = false;
      currentStrokeRef.current = null;
    }

    canvas.addEventListener("pointerdown", down);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", up);
    canvas.addEventListener("pointercancel", up);
    canvas.addEventListener("pointerleave", up);
    return () => {
      observer?.disconnect();
      canvas.removeEventListener("pointerdown", down);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerup", up);
      canvas.removeEventListener("pointercancel", up);
      canvas.removeEventListener("pointerleave", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  useEffect(() => {
    controlRef.current = {
      toDataURL: () => canvasRef.current!.toDataURL("image/png"),
      clear: () => {
        strokesRef.current = [];
        repaint();
        setHasInk(false);
        onChange?.(false);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlRef, onChange]);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <canvas
        ref={canvasRef}
        className="block w-full touch-none rounded-lg border border-slate-300 bg-white"
        aria-label="Signature pad"
      />
      {!hasInk && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-400">
          Sign with your finger or mouse
        </div>
      )}
    </div>
  );
}
